import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ThreeCircles } from 'react-loader-spinner';
import { getHierarchy } from './K1HierarchyInlineView.service';
import type { DestinationReturnIdOption } from '../ReturnIdDropdown/ReturnIdDropdown.types';
import { CustomButton } from 'reusable-components/CustomButtonComponent/button';
import { K1HierarchyFlowGraph } from './components/K1HierarchyFlowGraph/K1HierarchyFlowGraph';
import styles from './K1HierarchyInlineView.module.css';
import type { K1HierarchyInlineViewProps } from './K1HierarchyInlineView.types';
import { buildDocLookup, getCurrentClientId, getSelectedK1Sources } from './K1HierarchyInlineView.utils';

const K1HierarchyInlineView: React.FC<K1HierarchyInlineViewProps> = ({
  returnid,
  clientMasterId,
  refreshKey,
  showAllNodes = false,
  onShowAllNodesChange,
  documentsData = [],
  documentsLoading = false,
  onSelectReturnId,
  onMapActivities,
  onImportToTax,
  importingToTax = false,
  previewBeforeImport = true,
  onPreviewBeforeImportChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [allNodes, setAllNodes] = useState<any[]>([]);
  const [immediateNodes, setImmediateNodes] = useState<any[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onSelectReturnIdRef = useRef(onSelectReturnId);
  onSelectReturnIdRef.current = onSelectReturnId;

  const handleSelectReturnId = useCallback((rowData: any, option: DestinationReturnIdOption | null) => {
    if (!option) {
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        next.delete(String(rowData.DocumentId));
        return next;
      });
    } else {
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        next.add(String(rowData.DocumentId));
        return next;
      });
    }
    onSelectReturnIdRef.current?.(rowData, option);
  }, []);

  const handleToggleSelection = useCallback((documentId: string) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  const currentClientId = useMemo(() => getCurrentClientId(clientMasterId), [clientMasterId]);
  const docLookup = useMemo(() => buildDocLookup(documentsData), [documentsData]);
  const selectedSources = useMemo(
    () => getSelectedK1Sources(documentsData, selectedNodeIds),
    [documentsData, selectedNodeIds],
  );
  const displayedNodes = showAllNodes ? allNodes : immediateNodes;

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  useEffect(() => {
    if (!returnid || !clientMasterId) return;
    setLoading(true);
    getHierarchy(returnid, String(clientMasterId))
      .then((data: any) => {
        setAllNodes(data?.nodes ?? []);
        setImmediateNodes(data?.immediate_nodes ?? data?.nodes ?? []);
      })
      .finally(() => setLoading(false));
  }, [returnid, clientMasterId, refreshKey]);

  if (loading || documentsLoading) {
    return (
      <div className={styles.layout}>
        <div className={styles.graphArea}>
          <div className={styles.center}>
            <ThreeCircles
              height="50"
              width="50"
              visible={true}
              ariaLabel="three-circles-rotating"
              outerCircleColor="var(--theme1_tertiary)"
              innerCircleColor="var(--theme1_secondary)"
              middleCircleColor="var(--theme1_primary)"
            />
            <p className={styles.loadingText}>Loading hierarchy...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!displayedNodes.length) {
    return (
      <div className={styles.layout}>
        <div className={styles.graphArea}>
          <div className={styles.center}>
            <p className={styles.emptyText}>No hierarchy data available for the selected Return ID.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.layout}>
      <div className={`${styles.graphArea} ${(isFullscreen || showAllNodes) ? styles.graphAreaFull : ''}`}>
        {isFullscreen && (
          <div className={styles.graphControls}>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.toggleBtn} ${!showAllNodes ? styles.toggleBtnActive : ''}`}
                onClick={() => onShowAllNodesChange?.(false)}
                aria-pressed={!showAllNodes}
              >
                Only Parents
              </button>
              <button
                className={`${styles.toggleBtn} ${showAllNodes ? styles.toggleBtnActive : ''}`}
                onClick={() => onShowAllNodesChange?.(true)}
                aria-pressed={showAllNodes}
              >
                All Collections
              </button>
            </div>
          </div>
        )}

        <ReactFlowProvider key={showAllNodes ? 'all' : 'parents'}>
          <K1HierarchyFlowGraph
            hierarchyData={displayedNodes}
            docLookup={docLookup}
            currentClientId={currentClientId}
            readOnly={showAllNodes}
            selectedNodeIds={selectedNodeIds}
            onSelectReturnId={handleSelectReturnId}
            onToggleSelection={handleToggleSelection}
            onMapActivities={onMapActivities}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
        </ReactFlowProvider>
      </div>

      {!isFullscreen && !showAllNodes && (
        <div className={styles.rightPanel}>
          <p className={styles.rightPanelTitle}>Selected Sources</p>

          <div className={styles.selectedSourcesList}>
            {selectedSources.length === 0 ? (
              <p className={styles.rightPanelEmpty}>
                Select source to<br />import K1s into CCH
              </p>
            ) : (
              selectedSources.map((doc) => (
                <div key={doc.DocumentId ?? doc.SourceName} className={styles.selectedSourceItem}>
                  <p className={styles.selectedSourceName}>{doc.SourceName}</p>
                  <p className={styles.selectedSourceReturnId}>{doc.InternalK1SelectedReturnId}</p>
                </div>
              ))
            )}
          </div>

          <div className={styles.rightPanelFooter}>
            <label className={styles.previewLabel}>
              <input
                type="checkbox"
                checked={previewBeforeImport}
                onChange={(e) => onPreviewBeforeImportChange?.(e.target.checked)}
                className={styles.previewCheckbox}
              />
              Preview/edit data before importing
            </label>
            <CustomButton
              variant="default"
              type="button"
              content={importingToTax ? 'Importing...' : 'Import to CCH'}
              progress={importingToTax}
              disable={selectedSources.length === 0 || importingToTax}
              onclick={() => onImportToTax?.(selectedSources)}
              className={styles.importBtn}
              wrapperStyle={{ width: '100%' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default K1HierarchyInlineView;
